import { useCallback, useState } from 'react';

import { ObservationsV1IssueCloseReason } from '@spotify/confidence-graphql';
import {
  IssueFragment,
  useCloseIssue,
} from '@spotify/plugin-confidence-issues-react';

import { IssueCloseDialog } from '../components/IssueCloseDialog';

export type RequestIssueClose = (
  issue: Pick<IssueFragment, 'name'>,
  closeReason: ObservationsV1IssueCloseReason,
) => void;

type UseIssueClosureOptions = {
  closeFixedIssue?: ReturnType<typeof useCloseIssue>['closeIssue'];
  onClosed?: (closeReason: ObservationsV1IssueCloseReason) => void;
};

type PendingClosure = {
  issue: Pick<IssueFragment, 'name'>;
  closeReason: ObservationsV1IssueCloseReason;
};

export const useIssueClosure = ({
  closeFixedIssue,
  onClosed,
}: UseIssueClosureOptions = {}) => {
  const [pendingClosure, setPendingClosure] = useState<PendingClosure>();
  const { closeIssue, isMutating } = useCloseIssue({ onError: 'post-alert' });
  const requestClose = useCallback<RequestIssueClose>(
    (issue, closeReason) => {
      if (closeReason === ObservationsV1IssueCloseReason.Fixed) {
        void (closeFixedIssue ?? closeIssue)(issue.name, closeReason)
          .then(() => onClosed?.(closeReason))
          .catch(() => {});
      } else {
        setPendingClosure({ issue, closeReason });
      }
    },
    [closeFixedIssue, closeIssue, onClosed],
  );

  return {
    isMutating,
    requestClose,
    closeDialog: pendingClosure ? (
      <IssueCloseDialog
        issue={pendingClosure.issue}
        closeReason={pendingClosure.closeReason}
        onClosed={onClosed}
        onClose={() => setPendingClosure(undefined)}
       data-track-source-anchor="a6257b2e99;use-issue-closure-issue-close-dialog"/>
    ) : null,
  };
};
